import { t, N_ } from '../i18n/i18n';
/**
 * Why the thing you pressed did not happen (2026-09-01).
 *
 * Reported as abilities being broken: "when I press a spell, nothing happens,
 * and there is no notification explaining why". Most of those presses were
 * being refused correctly — mid-swing, on cooldown, out of resource — but the
 * refusal arrived as a raw enum in the scrolling combat log (`rejected:
 * insufficient_resource`), which is developer output in the one place a player
 * mid-fight will never read.
 *
 * This is the same information said in words, once, where the player is
 * already looking: directly above the action bar, because that is the button
 * they just pressed. Repeating the same reason keeps it on screen and pulses
 * rather than stacking, so mashing a key on cooldown reads as one continuing
 * refusal rather than a wall of identical lines.
 *
 * `pointer-events-none` throughout: it appears mid-fight and must never eat a
 * click meant for the world.
 */

/** How long a notice stays before it fades. Long enough to read four words, short enough to be gone before the next opening. */
const HOLD_MS = 1_600;
const FADE_MS = 260;

/**
 * The states an ability can be refused from, in the player's language.
 *
 * `busy` carries the actual state name from the server precisely so this can
 * say which busy — "mid-swing" and "staggered" are the same rule and very
 * different feelings, and a player who is told the wrong one will think the
 * game is lying.
 */
const BUSY_BY_STATE: Record<string, string> = {
  ATTACK_WINDUP: N_('Mid-swing'),
  ATTACK_ACTIVE: N_('Mid-swing'),
  ATTACK_RECOVERY: N_('Recovering from a swing'),
  PARRY_WINDOW: N_('Mid-parry'),
  PARRY_RECOVERY: N_('Recovering from a parry'),
  FEINT_RECOVERY: N_('Recovering from a feint'),
  STAGGERED: N_('Staggered'),
  BLOCKING: N_('Blocking'),
  CASTING: N_('Already casting'),
  DEAD: N_('You are down'),
  DISCONNECTED: N_('Disconnected'),
};

/**
 * Everything the server can refuse with. Kept exhaustive on purpose — an
 * unmapped reason falls back to the raw string, which is worse than useless to
 * a player but at least tells whoever is watching that a case was missed.
 */
const MESSAGE: Record<string, string> = {
  on_cooldown: N_('Still on cooldown'),
  global_cooldown: N_('Not ready yet'),
  // Names the remedy, not just the problem: 'No target' left a player who
  // had never heard of Tab with nowhere to go. Abilities also auto-acquire
  // the nearest monster now, so this should mostly appear when there is
  // genuinely nothing alive nearby.
  needs_target: N_('No target — press Tab to pick one'),
  invalid_target: N_('You cannot target that — press Tab for an enemy'),
  already_casting: N_('Already casting'),
  wrong_class: N_('Not one of your abilities'),
  unknown_ability: N_('Not one of your abilities'),
  malformed_payload: N_('Not one of your abilities'),
  no_charges: N_('None left'),
  rate_limited: N_('Too fast'),
  insufficient_stamina: N_('Not enough stamina'),
  out_of_range: N_('Too far away'),
  // 2026-10-16: abilities are line-of-sight now; a wall or a closed door
  // between caster and target refuses rather than casting through it.
  no_line_of_sight: N_('Something is in the way'),
  // Streamed but not yet seen — too far into the dark to be fair game.
  not_visible: N_('You cannot see it from here'),
  nobody_wounded_in_range: N_('Nobody wounded in range'),
  over_carry_capacity: N_('Your pack is full'),
  warden_guards_the_vault: N_('The Warden still guards it'),
  portcullis_not_open: N_('It is not open yet'),
  descend_failed: N_('The stairs will not take you'),
  not_downed: N_('They are not down'),
  not_your_squad: N_('Not your squadmate'),
  insurance_slots_full: N_('No insurance slots left'),
  not_insurable: N_('That cannot be insured'),
  not_owned: N_('You are not carrying that'),
  // Sent by the rooms' onUncaughtException: the action hit a server-side bug.
  // Vague on purpose — the player cannot act on a stack trace, and the detail
  // is already in the server log where someone can.
  server_error: N_('Something went wrong'),
};

/**
 * Pure so it can be tested without a DOM, and exported so the test can assert
 * the property that matters: every reason the server is capable of sending
 * resolves to a sentence, not to the enum leaking through to the player.
 */
export function messageForRejection(reason: string, state?: string, resourceKind?: string, touch = false): string {
  // A phone has no Tab key — the remedy names the on-screen Target plate
  // instead (2026-10-16). Same rule as everywhere else: an instruction the
  // player cannot follow is worse than none.
  if (touch && reason === 'needs_target') return t('No target — tap Target to pick one');
  if (touch && reason === 'invalid_target') return t('You cannot target that — tap Target for an enemy');
  if (reason === 'busy') {
    const busy = state ? BUSY_BY_STATE[state] : undefined;
    return busy ? t(busy) : t('Busy');
  }
  if (reason === 'insufficient_resource') {
    // "Not enough Rage" beats "insufficient resource" for the same reason the
    // HP bar prints a denominator: the player is being told about a specific
    // bar they can see.
    const named = resourceKind ? RESOURCE_NAME[resourceKind] : undefined;
    const kind = named
      ? t(named)
      : resourceKind ? resourceKind.charAt(0) + resourceKind.slice(1).toLowerCase() : t('resource');
    return t('Not enough {resource}', { resource: kind });
  }
  const message = MESSAGE[reason];
  return message ? t(message) : reason;
}

/** The bars a resource refusal can name. */
const RESOURCE_NAME: Record<string, string> = { MANA: N_('Mana'), RAGE: N_('Rage'), ENERGY: N_('Energy') };

/** Every reason the server can send, so a new one cannot ship unmapped. */
export const KNOWN_REJECTIONS: readonly string[] = [
  'already_casting', 'busy', 'descend_failed', 'global_cooldown', 'insufficient_resource',
  'insufficient_stamina', 'insurance_slots_full', 'invalid_target', 'malformed_payload',
  'needs_target', 'no_charges', 'no_line_of_sight', 'not_visible', 'nobody_wounded_in_range', 'not_downed', 'not_insurable',
  'not_owned', 'not_your_squad', 'on_cooldown', 'out_of_range', 'over_carry_capacity',
  'portcullis_not_open', 'rate_limited', 'server_error', 'unknown_ability',
  'warden_guards_the_vault', 'wrong_class',
];

/** The states an ability can be refused from, exported for the same reason. */
export const KNOWN_BUSY_STATES: readonly string[] = Object.keys(BUSY_BY_STATE);

export class AbilityNotice {
  private readonly root: HTMLDivElement;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private lastText = '';
  private readonly reducedMotion: boolean;
  /** Touch play: the Tab remedy becomes "tap Target". Set by the bootstrap from the touch-controls decision. */
  touch = false;

  constructor(container: HTMLElement) {
    this.reducedMotion = typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    this.root = document.createElement('div');
    this.root.className = [
      'fixed', 'left-1/2', '-translate-x-1/2', 'bottom-[7.5rem]', 'z-[1240]',
      'pointer-events-none', 'select-none', 'text-center',
      'px-4', 'py-1.5', 'rounded', 'text-sm', 'sm:text-base', 'tracking-wide',
    ].join(' ');
    this.root.dataset.hud = 'ability-notice';
    this.root.style.background = 'rgba(11,10,18,0.82)';
    this.root.style.boxShadow = 'inset 0 0 0 1px rgba(201,168,76,0.35)';
    this.root.style.color = '#ede0cd';
    this.root.style.opacity = '0';
    this.root.style.transition = `opacity ${FADE_MS}ms ease`;
    container.appendChild(this.root);
  }

  /**
   * @param reason       The server's machine reason.
   * @param state        Present on `busy` — which action was in the way.
   * @param resourceKind MANA / RAGE / ENERGY, so running dry names the bar the
   *                     player is looking at rather than saying "resource".
   */
  show(reason: string, state?: string, resourceKind?: string): void {
    this.showText(this.textFor(reason, state, resourceKind));
  }

  /**
   * An already-translated sentence, for things the player has to know that are
   * not server refusals — "Nothing in range", "{name} is down — hold E…"
   * (2026-10-16: those used to reach only the developer combat log).
   */
  showText(text: string, holdMs: number = HOLD_MS): void {
    const repeat = text === this.lastText && this.root.style.opacity !== '0';
    this.lastText = text;
    this.root.textContent = text;
    this.root.style.opacity = '1';

    // A repeat pulses instead of stacking — mashing a key on cooldown should
    // read as one refusal that keeps being true.
    if (repeat && !this.reducedMotion && typeof this.root.animate === 'function') {
      this.root.animate(
        [{ transform: 'translateX(-50%) scale(1)' }, { transform: 'translateX(-50%) scale(1.06)' }, { transform: 'translateX(-50%) scale(1)' }],
        { duration: 180, easing: 'ease-out' },
      );
    }

    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => { this.root.style.opacity = '0'; this.lastText = ''; }, holdMs);
  }

  private textFor(reason: string, state?: string, resourceKind?: string): string {
    return messageForRejection(reason, state, resourceKind, this.touch);
  }

  dispose(): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.root.remove();
  }
}
